import { FC, useMemo } from 'react'

import { useParams } from 'react-router-dom'

import {
  PrimaryScheduleSteps,
  PrimaryScheduleTabs,
} from 'src/Components/PrimaryScheduleTabs'
import { useRedirectFailedCheckout } from 'src/Hooks/useRedirectFailedCheckout'
import { useScheduleSpotifyCampaign } from 'src/Pages/Artist/SpotifySchedulePage/useSchaduleSpotifyCampaign'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { ScheduleSteps } from 'src/Types/ui'
import { ScheduleSuccessPage } from 'src/Components/ScheduleSuccessPage'
import { useSpotifyCampaignMinBudgetExperiment } from 'src/Hooks/Experiments/useSpotifyCampaignMinBudgetExperiment'
import {
  useManualMatchingV2Experiment,
  useIsManualMatchingV2Enabled,
} from 'src/Hooks/Experiments/useManualMatchingV2Experiment'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { useAppSelector } from 'src/Hooks/redux'
import { selectManualMatchingBudget } from 'src/Redux/selectedCuratorsSlice'

import { SpotifyScheduleSettingsPage } from './SpotifyScheduleSettingsPage'
import { SpotifyScheduleOrderCheckoutPage } from './SpotifyScheduleOrderCheckoutPage'
import { SpotifyScheduleTypePage } from './SpotifyScheduleTypePage'
import { OrderCheckoutRecoveryModal } from './OrderCheckoutRecoveryModal'

export const SpotifySchedulePage: FC = () => {
  useRedirectFailedCheckout()
  useScheduleSpotifyCampaign()
  useSpotifyCampaignMinBudgetExperiment()
  useManualMatchingV2Experiment()
  const { number } = useParams()

  const { campaignDetails, matchingType, isPreRelease } =
    useSpotifyCampaignStatus()
  const manualMatchingBudget = useAppSelector(selectManualMatchingBudget)
  const isManualMatchingEnabled = useIsManualMatchingV2Enabled()

  const isManualMatchingAvailable = isManualMatchingEnabled && !isPreRelease

  const defaultStep = useMemo(() => {
    if (!isManualMatchingAvailable) {
      return ScheduleSteps.SETTINGS
    }
    if (manualMatchingBudget !== null) {
      return ScheduleSteps.CHECKOUT
    }
    return matchingType ? ScheduleSteps.SETTINGS : ScheduleSteps.TYPE
  }, [matchingType, manualMatchingBudget, isManualMatchingAvailable])

  const steps: PrimaryScheduleSteps[] = useMemo(() => {
    const baseSteps: PrimaryScheduleSteps[] = [
      {
        title: 'Campaigns',
        path: ArtistRoutesEnum.CampaignsList,
      },
    ]

    if (isManualMatchingAvailable) {
      baseSteps.push({
        title: 'Campaign type',
        path: `${ArtistRoutesEnum.SpotifySchedule}/${number}?step=${ScheduleSteps.TYPE}`,
      })
    }

    baseSteps.push(
      {
        title: 'Campaign settings',
        path: `${ArtistRoutesEnum.SpotifySchedule}/${number}?step=${ScheduleSteps.SETTINGS}`,
      },
      {
        title: 'Checkout',
        path: `${ArtistRoutesEnum.SpotifySchedule}/${number}?step=${ScheduleSteps.CHECKOUT}`,
      },
    )

    return baseSteps
  }, [isManualMatchingAvailable, number])

  return (
    <>
      <OrderCheckoutRecoveryModal />
      <ScheduleSuccessPage
        startDate={campaignDetails?.start_date}
        platformName={CampaignPlatformTypeEnum.SPOTIFY}
      />
      {isManualMatchingAvailable ? (
        <PrimaryScheduleTabs steps={steps} defaultStep={defaultStep}>
          <SpotifyScheduleTypePage />
          <SpotifyScheduleSettingsPage />
          <SpotifyScheduleOrderCheckoutPage />
        </PrimaryScheduleTabs>
      ) : (
        <PrimaryScheduleTabs steps={steps} defaultStep={defaultStep}>
          <SpotifyScheduleSettingsPage />
          <SpotifyScheduleOrderCheckoutPage />
        </PrimaryScheduleTabs>
      )}
    </>
  )
}
